import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../services/api';
import { getAuth } from 'firebase/auth';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, isToday, isTomorrow, addDays } from 'date-fns';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TodayIcon from '@mui/icons-material/Today';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { useAuth } from '../../contexts/AuthContext';

// Helper function to create auth headers
const createAuthHeaders = async () => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  const token = await user.getIdToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

// Production API call to get provider appointments
const getProviderAppointments = async (providerId, startDate, endDate, status) => {
  try {
    // Format dates properly for API call
    const formattedStartDate = startDate instanceof Date ? startDate.toISOString() : startDate;
    const formattedEndDate = endDate instanceof Date ? endDate.toISOString() : endDate;
    
    // Make API call to the backend
    const headers = await createAuthHeaders();
    const response = await axios.get(`${API_BASE_URL}/api/appointments/provider/${providerId}`, {
      params: {
        start_date: formattedStartDate,
        end_time: formattedEndDate,
        status: status !== 'all' ? status : undefined
      },
      headers
    });
    
    return response.data;
  } catch (error) {
    console.error('Error fetching provider appointments:', error);
    // Return empty array on error
    return [];
  }
};

// Production API call to complete an appointment
const completeAppointment = async (appointmentId, notes) => {
  try {
    const headers = await createAuthHeaders();
    const response = await axios.put(`${API_BASE_URL}/api/appointments/${appointmentId}/complete`, { notes }, { headers });
    return response.data;
  } catch (error) {
    console.error('Error completing appointment:', error);
    throw error;
  }
};

// Production API call to cancel an appointment
const cancelAppointment = async (appointmentId, reason) => {
  try {
    const headers = await createAuthHeaders();
    const response = await axios.put(`${API_BASE_URL}/api/appointments/${appointmentId}/cancel`, { reason }, { headers });
    return response.data;
  } catch (error) {
    console.error('Error cancelling appointment:', error);
    throw error;
  }
};

// Production API call to update appointment notes
const updateAppointmentNotes = async (appointmentId, notes) => {
  try {
    const headers = await createAuthHeaders();
    const response = await axios.put(`${API_BASE_URL}/api/appointments/${appointmentId}/notes`, { notes }, { headers });
    return response.data;
  } catch (error) {
    console.error('Error updating appointment notes:', error);
    throw error;
  }
};

export default function AppointmentsPanel({ providerId }) {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(),
    endDate: addDays(new Date(), 30)
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [notes, setNotes] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId, tabValue, dateRange]);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      
      let status = 'all';
      switch (tabValue) {
        case 0: // Upcoming
          status = 'scheduled';
          break;
        case 1: // Completed
          status = 'completed';
          break;
        case 2: // Cancelled
          status = 'cancelled';
          break;
        default:
          status = 'all';
      }
      
      const data = await getProviderAppointments(
        providerId || currentUser.uid,
        dateRange.startDate,
        dateRange.endDate,
        status
      );
      
      setAppointments(data);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load appointments. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleStartDateChange = (date) => {
    setDateRange({
      ...dateRange,
      startDate: date
    });
  };

  const handleEndDateChange = (date) => {
    setDateRange({
      ...dateRange,
      endDate: date
    });
  };

  const handleOpenDialog = (type, appointment) => {
    setDialogType(type);
    setSelectedAppointment(appointment);
    setNotes(appointment.notes || '');
    setCancelReason('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedAppointment(null);
    setNotes('');
    setCancelReason('');
  };

  const handleComplete = async () => {
    try {
      setLoading(true);
      
      await completeAppointment(selectedAppointment.id, notes);
      
      // Update local state
      const updatedAppointments = appointments.map(appointment => 
        appointment.id === selectedAppointment.id 
          ? { ...appointment, status: 'completed', notes: notes } 
          : appointment
      );
      
      setAppointments(updatedAppointments);
      
      setSnackbar({
        open: true,
        message: 'Appointment marked as completed',
        severity: 'success'
      });
      
      handleCloseDialog();
    } catch (error) {
      console.error('Error completing appointment:', error);
      setSnackbar({
        open: true,
        message: 'Failed to complete appointment. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    try {
      setLoading(true);
      
      await cancelAppointment(selectedAppointment.id, cancelReason);
      
      // Update local state
      const updatedAppointments = appointments.map(appointment => 
        appointment.id === selectedAppointment.id 
          ? { ...appointment, status: 'cancelled', notes: cancelReason } 
          : appointment
      );
      
      setAppointments(updatedAppointments);
      
      setSnackbar({
        open: true,
        message: 'Appointment cancelled',
        severity: 'success'
      });
      
      handleCloseDialog();
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      setSnackbar({
        open: true,
        message: 'Failed to cancel appointment. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateNotes = async () => {
    try {
      setLoading(true);
      
      await updateAppointmentNotes(selectedAppointment.id, notes);
      
      // Update local state
      const updatedAppointments = appointments.map(appointment => 
        appointment.id === selectedAppointment.id 
          ? { ...appointment, notes: notes } 
          : appointment
      );
      
      setAppointments(updatedAppointments);
      
      setSnackbar({
        open: true,
        message: 'Appointment notes updated',
        severity: 'success'
      });
      
      handleCloseDialog();
    } catch (error) {
      console.error('Error updating appointment notes:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update appointment notes. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatAppointmentDate = (dateString) => {
    const date = new Date(dateString);
    
    if (isToday(date)) {
      return 'Today';
    } else if (isTomorrow(date)) {
      return 'Tomorrow';
    } else {
      return format(date, 'EEE, MMM d, yyyy');
    }
  };

  const formatAppointmentTime = (dateString) => {
    const date = new Date(dateString);
    return format(date, 'h:mm a');
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'scheduled':
        return <Chip label="Scheduled" color="primary" size="small" />;
      case 'completed':
        return <Chip label="Completed" color="success" size="small" />;
      case 'cancelled':
        return <Chip label="Cancelled" color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Typography variant="h5" component="h2" gutterBottom>
          Appointments
        </Typography>
        
        <Box sx={{ mb: 3 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="appointment tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Upcoming" icon={<ScheduleIcon />} iconPosition="start" />
            <Tab label="Completed" icon={<CheckCircleIcon />} iconPosition="start" />
            <Tab label="Cancelled" icon={<CancelIcon />} iconPosition="start" />
          </Tabs>
        </Box>
        
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TodayIcon sx={{ mr: 1 }} />
            <DatePicker
              label="From"
              value={dateRange.startDate}
              onChange={handleStartDateChange}
              renderInput={(params) => <TextField {...params} size="small" sx={{ width: 150 }} />}
            />
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DateRangeIcon sx={{ mr: 1 }} />
            <DatePicker
              label="To"
              value={dateRange.endDate}
              onChange={handleEndDateChange}
              renderInput={(params) => <TextField {...params} size="small" sx={{ width: 150 }} />}
            />
          </Box>
          
          <Button 
            variant="outlined" 
            onClick={fetchAppointments}
            disabled={loading}
          >
            Apply Filter
          </Button>
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : appointments.length === 0 ? (
          <Alert severity="info">
            No appointments found for the selected criteria.
          </Alert>
        ) : (
          <TableContainer component={Paper} elevation={2}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date & Time</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Service</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {appointments.map((appointment) => (
                  <TableRow key={appointment.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {formatAppointmentDate(appointment.start_time)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatAppointmentTime(appointment.start_time)} - {formatAppointmentTime(appointment.end_time)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {appointment.customer_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {appointment.customer_phone}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {appointment.service_type}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {appointment.location}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {getStatusChip(appointment.status)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex' }}>
                        {appointment.status === 'scheduled' && (
                          <>
                            <IconButton 
                              size="small" 
                              color="success" 
                              onClick={() => handleOpenDialog('complete', appointment)}
                              aria-label="mark as completed"
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              color="error" 
                              onClick={() => handleOpenDialog('cancel', appointment)}
                              aria-label="cancel appointment"
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                        <IconButton 
                          size="small" 
                          color="primary" 
                          onClick={() => handleOpenDialog('notes', appointment)}
                          aria-label="edit notes"
                        >
                          <EventNoteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        
        {/* Dialogs */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          {dialogType === 'complete' && (
            <>
              <DialogTitle>Complete Appointment</DialogTitle>
              <DialogContent>
                <Typography variant="body1" gutterBottom>
                  Mark this appointment with {selectedAppointment?.customer_name} as completed?
                </Typography>
                <TextField
                  label="Completion Notes"
                  multiline
                  rows={4}
                  fullWidth
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  margin="normal"
                  placeholder="Add any notes about the completed work"
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseDialog} disabled={loading}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleComplete} 
                  variant="contained" 
                  color="success"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                >
                  {loading ? 'Processing...' : 'Complete'}
                </Button>
              </DialogActions>
            </>
          )}
          
          {dialogType === 'cancel' && (
            <>
              <DialogTitle>Cancel Appointment</DialogTitle>
              <DialogContent>
                <Typography variant="body1" gutterBottom>
                  Are you sure you want to cancel this appointment with {selectedAppointment?.customer_name}?
                </Typography>
                <TextField
                  label="Cancellation Reason"
                  multiline
                  rows={4}
                  fullWidth
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  margin="normal"
                  placeholder="Enter reason for cancellation"
                  required
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseDialog} disabled={loading}>
                  Back
                </Button>
                <Button 
                  onClick={handleCancel} 
                  variant="contained" 
                  color="error"
                  disabled={loading || !cancelReason.trim()}
                  startIcon={loading ? <CircularProgress size={20} /> : <CancelIcon />}
                >
                  {loading ? 'Processing...' : 'Cancel Appointment'}
                </Button>
              </DialogActions>
            </>
          )}
          
          {dialogType === 'notes' && (
            <>
              <DialogTitle>Appointment Notes</DialogTitle>
              <DialogContent>
                <Typography variant="body1" gutterBottom>
                  Edit notes for appointment with {selectedAppointment?.customer_name}
                </Typography>
                <TextField
                  label="Notes"
                  multiline
                  rows={4}
                  fullWidth
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  margin="normal"
                  placeholder="Add any notes about this appointment"
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseDialog} disabled={loading}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleUpdateNotes} 
                  variant="contained" 
                  color="primary"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <EventNoteIcon />}
                >
                  {loading ? 'Saving...' : 'Save Notes'}
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
        
        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbar.severity} 
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
}
